'use client';

import { isRetryableError, isNetworkError } from './network-errors';
import {
  waitForVisibility,
  waitForNetwork,
  isDocumentHidden,
  isNetworkOffline,
} from './visibility';

interface FetchOptions {
  timeout?: number;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

interface FetchWithTimeoutParams {
  url: string;
  options?: RequestInit;
  timeout?: number;
}

const MAX_RETRIES = 2;
const BASE_DELAY = 1000;
const DEFAULT_TIMEOUT = 90000;

export async function fetchWithTimeout({
  url,
  options = {},
  timeout = DEFAULT_TIMEOUT,
}: FetchWithTimeoutParams): Promise<Response> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (options.signal?.aborted) {
      throw options.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    const signal = options.signal
      ? AbortSignal.any([controller.signal, options.signal])
      : controller.signal;

    try {
      const response = await fetch(url, { ...options, signal });
      return response;
    } catch (error) {
      if (options.signal?.aborted) {
        throw error;
      }

      const shouldRetry = attempt < MAX_RETRIES && isRetryableError(error);
      if (!shouldRetry) {
        throw error;
      }

      if (isNetworkError(error)) {
        if (isNetworkOffline()) {
          await waitForNetwork(options.signal ?? undefined);
        }
        if (isDocumentHidden()) {
          await waitForVisibility(options.signal ?? undefined);
        }
        await new Promise(resolve => setTimeout(resolve, BASE_DELAY));
      } else {
        const delay = BASE_DELAY * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } finally {
      clearTimeout(timeoutId);
    }
  }

  throw new Error('Unreachable: all retry attempts should throw or return');
}

async function handleResponse<T>(response: Response, url: string): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`HTTP ${response.status} from ${url}: ${errorText}`);
  }

  return response.json();
}

export async function post<T = unknown>(
  url: string,
  data?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<T> {
  const response = await fetchWithTimeout({
    url,
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      signal: options?.signal,
    },
    timeout: options?.timeout,
  });

  return handleResponse<T>(response, url);
}
